import React, {useState} from 'react';
import {Controller, useForm, useWatch} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';
import type {ClassroomV1} from '@albert-io/models';

import {
  addToast,
  Anchor,
  Banner,
  Button,
  Heading,
  IconButton,
  Input,
  Modal,
  SelectField,
  Text,
  TextField
} from '@albert-io/atomic';
import {formatClassroomCodeForDisplay} from 'client/Classrooms/ClassroomUtil';
import LicenseStatusRadioGroup from 'client/Classrooms/Teacher/ClassroomsList/LicenseStatus/LicenseStatusRadioGroup';
import CourseDetail from 'client/GoogleClassroom/Courses/CourseDetail.react';
import GenerateJoinCodeDialogue from 'client/components/GenerateJoinCodeDialogue/GenerateJoinCodeDialogue.react';
import {normalizeError} from 'lib/normalizeError';

import {SettingsActionsMenu} from './SettingsActionsMenu';
import type {ClassSettings, SettingsClassroom} from './useClassSettings';

const NAME_MAX = 40;

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'You must enter a classroom name')
    .max(NAME_MAX, `Classroom name must not be greater than ${NAME_MAX} characters`),
  schoolId: z.string(),
  applyStudentsToSchoolLicense: z.boolean()
});

type FormValues = z.infer<typeof schema>;

interface Props {
  settings: ClassSettings;
}

export const ClassSettingsForm = ({settings}: Props) => {
  // Settings only renders this once the classroom has loaded.
  const classroom = settings.classroom as SettingsClassroom;

  const [showRegenerate, setShowRegenerate] = useState(false);
  const [showCoteacherError, setShowCoteacherError] = useState(false);

  const {control, handleSubmit, formState, setValue} = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: classroom.getName(),
      schoolId: settings.currentSchoolId,
      applyStudentsToSchoolLicense: classroom.apply_students_to_school_license
    }
  });

  // The School select is editable for multi-school teachers, so the license warning has to track the
  // selected school rather than the saved one.
  const selectedSchoolId = useWatch({control, name: 'schoolId'});

  const onSubmit = async (values: FormValues) => {
    setShowCoteacherError(false);
    const result = await settings.save(values);
    if (result.ok) {
      addToast({color: 'positive', title: 'Success', message: 'Your classroom has been updated'});
      return;
    }
    addToast({
      color: 'negative',
      title: 'Error',
      message: result.error
        ? normalizeError(result.error).message
        : 'There was a problem updating your classroom'
    });
    if (result.hasUnapprovedCoteacherError) {
      setShowCoteacherError(true);
      // The school change was rejected, so revert the select to the still-persisted school.
      setValue('schoolId', settings.currentSchoolId);
    }
  };

  return (
    <form
      className='gap-x3 m-auto flex max-w-[600px] flex-col'
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className='gap-x2 pb-x2 flex items-center justify-between'>
        <Heading size='s'>Class Settings</Heading>
        <div className='gap-x1 flex items-center'>
          <SettingsActionsMenu
            classroom={classroom}
            onArchive={settings.archive}
            onUnarchive={settings.unarchive}
            onDelete={settings.deleteClassroom}
            onRemoveSelf={settings.removeSelf}
          />
          <Button type='submit' disabled={formState.isSubmitting}>
            Save
          </Button>
        </div>
      </div>

      <Controller
        name='name'
        control={control}
        render={({field, fieldState}) => (
          <TextField
            label='Class Name'
            name={field.name}
            value={field.value}
            maxLength={NAME_MAX}
            error={Boolean(fieldState.error)}
            message={fieldState.error?.message}
            onBlur={field.onBlur}
            onChange={(event) => field.onChange(event.target.value)}
          />
        )}
      />

      <Controller
        name='schoolId'
        control={control}
        render={({field}) => (
          <SelectField
            label='School'
            border
            name={field.name}
            value={field.value}
            options={settings.schools}
            disabled={!settings.isSchoolEditable}
            onBlur={field.onBlur}
            onChange={(event) => {
              field.onChange(event.target.value);
              // A pending school change clears the prior unapproved-co-teacher error.
              setShowCoteacherError(false);
            }}
          />
        )}
      />

      {showCoteacherError && (
        <Banner type='negative'>
          <Banner.Icon icon='exclamation' />
          <div>
            School association was not updated. All co-teachers must be approved by the
            school.&nbsp;
            <Anchor
              as='a'
              href='https://help.albert.io/en/articles/2459978-co-teacher-classrooms'
              target='_blank'
              rel='noopener noreferrer'
              inheritColor
              underlined
            >
              Learn more
            </Anchor>
            .
          </div>
        </Banner>
      )}

      {classroom.getGoogleCourseId() ? (
        // The legacy section's own bottom margin would stack with this column's gap.
        <div className='[&_.google-classroom-course-detail]:mb-x0'>
          <CourseDetail courseId={classroom.getGoogleCourseId()} />
        </div>
      ) : null}

      <div className='gap-x0-half flex flex-col'>
        <Text as='label' htmlFor='class-enrollment-code' size='m'>
          Class Enrollment Code
        </Text>
        <div className='gap-x1 flex items-center'>
          <Input
            readOnly
            id='class-enrollment-code'
            value={formatClassroomCodeForDisplay(classroom.getInviteCode())}
            className='w-[256px]'
          />
          <IconButton
            icon='arrows-rotate'
            iconStyle='regular'
            label='Regenerate enrollment code'
            color='secondary'
            variant='outlined'
            roundness='square'
            onClick={() => setShowRegenerate(true)}
          />
        </div>
      </div>

      <Controller
        name='applyStudentsToSchoolLicense'
        control={control}
        render={({field}) => (
          <LicenseStatusRadioGroup
            isUserForClassLicensed={settings.isUserForClassLicensed}
            isLicensed={field.value}
            setIsLicensed={field.onChange}
            isSchoolLicenseInactive={settings.isSchoolLicenseInactive(selectedSchoolId)}
            classroom={classroom as unknown as ClassroomV1}
          />
        )}
      />

      {showRegenerate && (
        <Modal
          ariaLabel='Generate a new enrollment code'
          handleClose={() => setShowRegenerate(false)}
        >
          {({modalContentStyle}) => (
            <GenerateJoinCodeDialogue
              className={modalContentStyle}
              classroom={classroom}
              onSuccess={() => setShowRegenerate(false)}
              handleClose={() => setShowRegenerate(false)}
              hideCloseBtn={false}
              inModal
            />
          )}
        </Modal>
      )}
    </form>
  );
};
